/**
 * GoalDetailModal - Detail view for a goal with linked habits, tasks, and projects
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Target, Check, Pause, Archive, X, Link2, Loader2 } from 'lucide-react';
import { AdaptiveModal } from './AdaptiveModal';
import { goalsAPI, habitsAPI, todosAPI } from '../services/api';
import type { GoalStatus, Habit, Todo } from '../types';

interface GoalDetailModalProps {
  goalId: string;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_CONFIG: Record<GoalStatus, { label: string; color: string; icon: React.ElementType }> = {
  active: { label: 'Active', color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/40', icon: Target },
  achieved: { label: 'Achieved', color: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/40', icon: Check },
  paused: { label: 'Paused', color: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/40', icon: Pause },
  abandoned: { label: 'Abandoned', color: 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-800', icon: Archive },
};

function LinkedSection<T extends { id: string }>({
  title,
  items,
  renderItem,
  onRemove,
  availableItems,
  renderAvailable,
  onAdd,
}: {
  title: string;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  onRemove: (item: T) => void;
  availableItems: T[];
  renderAvailable: (item: T) => string;
  onAdd: (item: T) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</h4>
        {availableItems.length > 0 && (
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="text-xs text-blue-500 hover:text-blue-600 dark:hover:text-blue-400"
          >
            {showAdd ? 'Cancel' : '+ Add'}
          </button>
        )}
      </div>
      {showAdd && (
        <select
          className="w-full text-sm px-2 py-1.5 mb-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
          value=""
          onChange={(e) => {
            const item = availableItems.find((i) => i.id === e.target.value);
            if (item) {
              onAdd(item);
              setShowAdd(false);
            }
          }}
        >
          <option value="">Select...</option>
          {availableItems.map((item) => (
            <option key={item.id} value={item.id}>
              {renderAvailable(item)}
            </option>
          ))}
        </select>
      )}
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-1">None linked</p>
      ) : (
        <div className="space-y-1">
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-2 group py-0.5">
              <div className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">
                {renderItem(item)}
              </div>
              <button
                onClick={() => onRemove(item)}
                className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-opacity"
                title="Unlink"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function GoalDetailModal({ goalId, isOpen, onClose }: GoalDetailModalProps) {
  const queryClient = useQueryClient();

  const { data: goal, isLoading } = useQuery({
    queryKey: ['goals', goalId],
    queryFn: () => goalsAPI.get(goalId),
    enabled: isOpen,
  });

  // Fetch available items for linking
  const { data: allHabits = [] } = useQuery({
    queryKey: ['habits', { activeOnly: true }],
    queryFn: () => habitsAPI.getAll({ active_only: true }),
    enabled: isOpen,
  });

  const { data: allTodos = [] } = useQuery({
    queryKey: ['todos', { forGoalLink: true }],
    queryFn: () => todosAPI.getAll({ status: ['not_started', 'in_progress', 'waiting'] }),
    enabled: isOpen,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['goals'] });
    queryClient.invalidateQueries({ queryKey: ['habits'] });
    queryClient.invalidateQueries({ queryKey: ['todos'] });
  };

  if (!isOpen) return null;

  const linkedHabitIds = new Set(goal?.habits?.map((h: Habit) => h.id) ?? []);
  const linkedTodoIds = new Set(goal?.todos?.map((t: Todo) => t.id) ?? []);

  const availableHabits = allHabits.filter((h) => !linkedHabitIds.has(h.id));
  const availableTodos = allTodos.filter((t) => !linkedTodoIds.has(t.id) && !t.goal_id);

  const handleLinkHabit = async (habit: Habit) => {
    await habitsAPI.update(habit.id, { goal_id: goalId });
    invalidateAll();
  };

  const handleUnlinkHabit = async (habit: Habit) => {
    await habitsAPI.update(habit.id, { goal_id: null });
    invalidateAll();
  };

  const handleLinkTodo = async (todo: Todo) => {
    await todosAPI.update(todo.id, { goal_id: goalId });
    invalidateAll();
  };

  const handleUnlinkTodo = async (todo: Todo) => {
    await todosAPI.update(todo.id, { goal_id: null });
    invalidateAll();
  };

  const statusConfig = goal ? STATUS_CONFIG[goal.status] : null;

  return (
    <AdaptiveModal isOpen={isOpen} onClose={onClose} title="" size="md">
      {isLoading || !goal ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Header */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              {statusConfig && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                  <statusConfig.icon className="w-3.5 h-3.5" />
                  {statusConfig.label}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{goal.name}</h2>
            {goal.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{goal.description}</p>
            )}
            {goal.target_date && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Target: {new Date(goal.target_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700" />

          {/* Linked Items */}
          <div className="space-y-4">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Link2 className="w-4 h-4" />
              Linked Items
            </div>

            <LinkedSection<Habit>
              title="Habits"
              items={goal.habits ?? []}
              renderItem={(h) => h.name}
              onRemove={handleUnlinkHabit}
              availableItems={availableHabits}
              renderAvailable={(h) => h.name}
              onAdd={handleLinkHabit}
            />

            <LinkedSection<Todo>
              title="Tasks"
              items={goal.todos ?? []}
              renderItem={(t) => t.title}
              onRemove={handleUnlinkTodo}
              availableItems={availableTodos}
              renderAvailable={(t) => t.title}
              onAdd={handleLinkTodo}
            />
          </div>
        </div>
      )}
    </AdaptiveModal>
  );
}
